// eslint-disable-next-line max-classes-per-file
import {
  IsArray,
  IsBoolean,
  IsDate,
  IsDecimal,
  IsEnum,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  IsUUID,
  Max,
  MaxLength,
  Min,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import type { PaginatedResultSubset } from '@pflegenavi/shared/utils';
import {
  PaginatedQuery,
  RangeDateFilter,
  RangeIntFilter,
} from '@pflegenavi/shared/utils';
import { Transform } from 'class-transformer';
import { PaymentStatus } from '@pflegenavi/shared/api';

export enum ReceiptBatchState {
  Draft = 'draft',
  InReview = 'in_review',
  ChangeRequested = 'change_requested',
  Declined = 'declined',
  Accepted = 'accepted',
}

export class ReceiptBatchDto {
  @IsNotEmpty()
  @IsString()
  @IsUUID(4)
  id!: string;
  @IsNotEmpty()
  @IsString()
  title!: string;
  @IsNotEmpty()
  @IsDate()
  createdOn!: Date;
  @IsNotEmpty()
  @IsDate()
  updatedOn!: Date;
  @IsNotEmpty()
  @IsString()
  @IsUUID(4)
  nursingHomeId!: string;
  @IsNotEmpty()
  @IsString()
  nursingHomeName!: string;
  @IsNotEmpty()
  @IsString()
  tenant!: string;

  @IsOptional()
  @IsDate()
  receiptDate?: Date;

  @IsOptional()
  @IsString({ each: true })
  @IsUUID(4, { each: true })
  @IsArray()
  receiptImageIds?: string[];

  @IsOptional()
  @IsDate()
  acceptedDate?: Date;

  @IsEnum(ReceiptBatchState)
  state!: ReceiptBatchState;

  @IsOptional()
  @IsBoolean()
  generateReceipts!: boolean;
}

export enum Gender {
  Female = 'Female',
  Male = 'Male',
  Other = 'Other',
}

export class GetReceiptBatchEntryResultResidentDto {
  @IsString()
  @IsUUID(4)
  id!: string;

  @IsNotEmpty()
  @IsString()
  firstName!: string;

  @IsNotEmpty()
  @IsString()
  lastName!: string;

  @IsNotEmpty()
  @IsEnum(Gender)
  gender!: Gender;
}

export class GetReceiptBatchEntryResultLineItemTaxDto {
  @IsNumber()
  @IsDecimal()
  taxAmount!: number;

  @IsUUID(4)
  countryId!: string;

  @IsString()
  taxName!: string;

  @IsOptional()
  @IsString()
  explanationShort?: string;

  @IsOptional()
  @IsString()
  explanationLong?: string;

  @IsBoolean()
  displayExplanation!: boolean;

  @IsOptional()
  @IsString()
  paragraph?: string;

  @IsOptional()
  @IsString()
  taxAbbreviation?: string;
}

export class GetReceiptBatchEntryResultLineItemDto {
  @IsUUID(4)
  id!: string;

  @IsInt()
  @IsPositive()
  count!: number;

  @IsString()
  name!: string | undefined;

  @IsInt()
  @Min(0)
  @Max(1000000)
  amountPerItemInCent!: number;

  @IsOptional()
  @ValidateNested({ each: true })
  taxes?: GetReceiptBatchEntryResultLineItemTaxDto[];
}

export class GetReceiptBatchEntryResultDto {
  @IsNotEmpty()
  @IsUUID(4)
  id!: string;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  @Max(1000000)
  /** @deprecated */
  amount?: number;

  @IsNumber()
  @IsOptional()
  additionalFeeAmount?: number; // This is shared between nursing home and service provider receipt batch endpoints. Only the nursing home endpoints have fees.

  @IsNumber()
  @IsOptional()
  includedFeeAmount?: number; // This is shared between nursing home and service provider receipt batch endpoints. Only the nursing home endpoints have fees.

  @IsOptional()
  @IsString()
  @MaxLength(255)
  /** @deprecated */
  notes?: string;

  @IsOptional()
  @IsArray()
  receiptImageIds?: string[];

  @IsOptional()
  @IsString()
  @IsUUID(4)
  residentId?: string;

  @IsOptional()
  @ValidateNested()
  resident?: GetReceiptBatchEntryResultResidentDto;

  @ValidateNested({ each: true })
  lineItems!: GetReceiptBatchEntryResultLineItemDto[];

  @IsOptional()
  @IsDate()
  receiptDate?: Date;
}

export class GetReceiptBatchResultDto extends ReceiptBatchDto {
  @IsNotEmpty()
  @IsArray()
  @ValidateNested({ each: true })
  receiptBatchEntries!: GetReceiptBatchEntryResultDto[];

  @IsNotEmpty()
  @IsBoolean()
  useIndividualDates!: boolean;
}

export class ReceiptBatchPayment {
  @IsString()
  id!: string;

  @IsEnum(PaymentStatus)
  status!: PaymentStatus;

  @IsInt()
  amountPaid!: number;

  @IsInt()
  @IsOptional()
  cancelledAmount!: number | null;

  @IsInt()
  @IsOptional()
  deletedAmount!: number | null;

  @IsInt()
  @IsOptional()
  deletedEntryCount!: number | null;
}

export class PaginatedReceiptBatchEntryDto {
  @IsNotEmpty()
  @IsString()
  @IsUUID(4)
  id!: string;
  @IsNotEmpty()
  @IsString()
  title!: string;
  @IsNotEmpty()
  @IsDate()
  createdOn!: Date;
  @IsNotEmpty()
  @IsDate()
  updatedOn!: Date;
  @IsNotEmpty()
  @IsString()
  @IsUUID(4)
  nursingHomeId!: string;
  @IsNotEmpty()
  @IsString()
  nursingHomeName!: string;
  @IsNotEmpty()
  @IsString()
  tenant!: string;

  @IsOptional()
  @IsDate()
  receiptDate?: Date;
  @IsOptional()
  @IsDate()
  minReceiptDate?: Date;
  @IsOptional()
  @IsDate()
  maxReceiptDate?: Date;

  @IsNotEmpty()
  @IsBoolean()
  useIndividualDates!: boolean;

  @IsOptional()
  @IsString({ each: true })
  @IsUUID(4, { each: true })
  @IsArray()
  receiptImageIds?: string[];

  @IsEnum(ReceiptBatchState)
  state!: ReceiptBatchState;

  @IsOptional()
  @IsDate()
  acceptedDate?: Date;
  @IsNotEmpty()
  @IsInt()
  entryCount!: number;
  @IsNotEmpty()
  @IsInt()
  invalidEntryCount!: number;
  @IsNotEmpty()
  @IsInt()
  totalAmount!: number;

  @IsNotEmpty()
  @IsInt()
  paidOutEntryCount!: number;

  @IsOptional()
  @IsBoolean()
  generateReceipts!: boolean;

  @IsOptional()
  @ValidateNested()
  payment?: ReceiptBatchPayment;
}

export type SortOrder = 'asc' | 'desc';
export type SortBy =
  | 'receiptDate'
  | 'totalAmount'
  | 'entryCount'
  | 'acceptedDate'
  | 'updatedOn';

export class GetReceiptBatchQueryDto extends PaginatedQuery {
  @IsOptional()
  @Transform(({ value }) => (value === '' ? undefined : value))
  @ValidateIf((value) => value !== null)
  @IsEnum(ReceiptBatchState, { each: true })
  state?: ReceiptBatchState[];
  @IsOptional()
  @IsUUID(4, { each: true })
  nursingHomeIds?: string[];
  @IsOptional()
  @IsUUID(4, { each: true })
  globalServiceProviderIds?: string[];
  @IsOptional()
  @ValidateNested()
  acceptedDateRange?: RangeDateFilter<Date>;
  @IsOptional()
  @ValidateNested()
  receiptDateRange?: RangeDateFilter<Date>;
  @IsOptional()
  @ValidateNested()
  amount?: RangeIntFilter<number>;
  @IsOptional()
  @ValidateNested()
  receiptCount?: RangeIntFilter<number>;
  @IsOptional()
  @IsIn(['asc', 'desc'])
  sortOrder?: SortOrder;
  @IsOptional()
  @IsString()
  sort?: SortBy;
}

export class PostReceiptBatchBodyDto {
  @IsNotEmpty()
  @IsString()
  title!: string;
  @IsNotEmpty()
  @IsString()
  @IsUUID(4)
  nursingHomeId!: string;
  @IsNotEmpty()
  @IsDate()
  receiptDate!: Date;
  @IsOptional()
  @IsString({ each: true })
  @IsUUID(4, { each: true })
  @IsArray()
  receiptImageIds?: string[];
  @IsNotEmpty()
  @IsUUID(4)
  globalServiceProviderId!: string;
  @IsOptional()
  @IsBoolean()
  generateReceipts?: boolean;
}

export class PutReceiptBatchBodyDto {
  @IsNotEmpty()
  @IsString()
  title!: string;
  @IsNotEmpty()
  @IsString()
  @IsUUID(4)
  nursingHomeId!: string;
  @IsOptional()
  @IsDate()
  receiptDate?: Date;
  @IsNotEmpty()
  @IsUUID(4)
  globalServiceProviderId!: string;
  @IsOptional()
  @IsString({ each: true })
  @IsUUID(4, { each: true })
  @IsArray()
  receiptImageIds?: string[];
  @IsBoolean()
  generateReceipts!: boolean;
  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  receiptBatchEntries?: PutReceiptBatchEntryDto[];
}

export class PatchReceiptBatchBodyDto {
  @IsOptional()
  @IsNotEmpty()
  @IsString()
  title?: string;
  @IsOptional()
  @IsNotEmpty()
  @IsDate()
  receiptDate?: Date;
  @IsOptional()
  @IsString({ each: true })
  @IsUUID(4, { each: true })
  @IsArray()
  receiptImageIds?: string[];
  @IsOptional()
  @IsBoolean()
  generateReceipts?: boolean;
}

export class ReceiptBatchEntryDto {
  @IsNotEmpty()
  @IsUUID(4)
  id!: string;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  @Max(1000000)
  /** @deprecated */
  amount?: number;

  @IsOptional()
  @IsString()
  @MaxLength(255)
  /** @deprecated */
  notes?: string;

  @IsOptional()
  @IsArray()
  receiptImageIds?: string[];

  @IsOptional()
  @IsString()
  @IsUUID(4)
  residentId?: string;

  @ValidateNested({ each: true })
  lineItems!: GetReceiptBatchEntryResultLineItemDto[];
}

export class PutPostReceiptBatchEntryResultLineItemTaxDto {
  @IsNumber()
  @IsDecimal()
  taxAmount!: number;

  @IsUUID(4)
  countryId!: string;

  @IsString()
  taxName!: string;

  @IsOptional()
  @IsString()
  explanationShort?: string;

  @IsOptional()
  @IsString()
  explanationLong?: string;

  @IsBoolean()
  displayExplanation!: boolean;

  @IsOptional()
  @IsString()
  paragraph?: string;

  @IsOptional()
  @IsString()
  taxAbbreviation?: string;
}

export class PutReceiptBatchEntryResultLineItemDto {
  @IsOptional()
  @IsUUID(4)
  id?: string;
  @IsInt()
  @IsPositive()
  count!: number;
  @IsString()
  name!: string | undefined;
  @IsInt()
  @Min(0)
  @Max(1000000)
  amountPerItemInCent!: number;
  @IsOptional()
  @ValidateNested({ each: true })
  taxes?: PutPostReceiptBatchEntryResultLineItemTaxDto[];
}

export class PutReceiptBatchEntryDto {
  @IsOptional()
  @IsString()
  @IsUUID(4)
  id?: string;

  @IsOptional()
  @IsNumber()
  @IsPositive()
  @Max(1000000)
  /** @deprecated */
  amount?: number;

  @IsOptional()
  @IsString()
  @MaxLength(255)
  /** @deprecated */
  notes?: string;

  @IsOptional()
  @IsArray()
  receiptImageIds?: string[];

  @IsOptional()
  @IsString()
  @IsUUID(4)
  residentId?: string;

  @IsOptional()
  @ValidateNested({ each: true })
  lineItems?: PutReceiptBatchEntryResultLineItemDto[];
}

export class PatchReceiptBatchEntryDto {
  @IsOptional()
  @IsNumber()
  @IsPositive()
  @Max(1000000)
  /** @deprecated */
  amount?: number | null;

  @IsOptional()
  @IsString()
  @MaxLength(255)
  /** @deprecated */
  notes?: string | null;

  @IsOptional()
  @IsArray()
  receiptImageIds?: string[];

  @IsOptional()
  @IsString()
  @IsUUID(4)
  residentId?: string | null;

  @IsOptional()
  @ValidateNested({ each: true })
  lineItems?: PutReceiptBatchEntryResultLineItemDto[];
}

export class PostReceiptBatchEntryResultLineItemDto {
  @IsInt()
  @IsPositive()
  count!: number;

  @IsString()
  name!: string | undefined;

  @IsInt()
  @Min(0)
  @Max(1000000)
  amountPerItemInCent!: number;

  @IsOptional()
  @ValidateNested({ each: true })
  taxes?: PutPostReceiptBatchEntryResultLineItemTaxDto[];
}

export class PostReceiptBatchEntryDto {
  @IsOptional()
  @IsNumber()
  @IsPositive()
  @Max(1000000)
  /** @deprecated */
  amount?: number;

  @IsOptional()
  @IsString()
  @MaxLength(255)
  /** @deprecated */
  notes?: string;

  @IsOptional()
  @IsArray()
  receiptImageIds?: string[];

  @IsOptional()
  @IsString()
  @IsUUID(4)
  residentId?: string;

  @IsOptional()
  @ValidateNested({ each: true })
  lineItems?: PostReceiptBatchEntryResultLineItemDto[];
}

export const ReceiptBatchControllerDefinition = {
  controller: 'receipt-batches',
  endpoints: {
    getReceiptBatchList: {
      method: 'GET',
      url: '',
      response:
        undefined as unknown as PaginatedResultSubset<PaginatedReceiptBatchEntryDto>,
      body: undefined,
      query: undefined as unknown as GetReceiptBatchQueryDto,
    },
    getReceiptBatch: {
      method: 'GET',
      url: ':batchId',
      response: undefined as unknown as GetReceiptBatchResultDto,
      body: undefined,
      query: undefined,
    },
    postReceiptBatch: {
      method: 'POST',
      url: '',
      response: undefined as unknown as ReceiptBatchDto,
      body: undefined as unknown as PostReceiptBatchBodyDto,
      query: undefined,
    },
    putReceiptBatch: {
      method: 'PUT',
      url: ':batchId',
      response: undefined as unknown as GetReceiptBatchResultDto,
      body: undefined as unknown as PutReceiptBatchBodyDto,
      query: undefined,
    },
    patchReceiptBatch: {
      method: 'PATCH',
      url: ':batchId',
      response: undefined as unknown as ReceiptBatchDto,
      body: undefined as unknown as PatchReceiptBatchBodyDto,
      query: undefined,
    },
    postReceiptBatchEntry: {
      method: 'PATCH',
      url: ':batchId/receipt',
      response: undefined as unknown as ReceiptBatchEntryDto,
      body: undefined as unknown as PostReceiptBatchEntryDto,
      query: undefined,
    },
    patchReceiptBatchEntry: {
      method: 'PATCH',
      url: ':batchId/receipt/:entryId',
      response: undefined as unknown as {
        success: boolean;
      },
      body: undefined as unknown as PatchReceiptBatchEntryDto,
      query: undefined,
    },
    deleteReceiptBatchEntry: {
      method: 'DELETE',
      url: ':batchId/receipt/:entryId',
      response: undefined as unknown as {
        success: boolean;
      },
      body: undefined,
      query: undefined,
    },
    generateReceiptBatchPdf: {
      description: 'Generate a PDF for a receipt batch.',
      method: 'POST',
      url: ':batchId/pdf',
      response: undefined as unknown as Blob,
      body: undefined,
      query: undefined,
    },
    generateReceiptPdf: {
      description: 'Generate a PDF for a receipt batch entry.',
      method: 'POST',
      url: ':batchId/receipt/:entryId/pdf',
      response: undefined as unknown as Blob,
      body: undefined,
      query: undefined,
    },
    generateAndUploadReceiptPdf: {
      description:
        'Generate a PDF for a receipt batch entry and upload it to S3.',
      method: 'POST',
      url: ':batchId/receipt/:entryId/pdf/upload',
      response: undefined as unknown as {
        imageId: string;
      },
      body: undefined,
      query: undefined,
    },
    deleteReceiptBatch: {
      method: 'DELETE',
      url: ':batchId',
      response: undefined as unknown as {
        success: boolean;
      },
      body: undefined,
      query: undefined,
    },
    submitReceiptBatch: {
      description: 'Submit a receipt batch to the nursing home.',
      method: 'POST',
      url: ':batchId/submit',
      response: undefined as unknown as {
        success: boolean;
      },
      body: undefined,
      query: undefined,
    },
    retractReceiptBatch: {
      description:
        'Retract a receipt batch that was previously submitted. ' +
        'The nursing home will still be able to see the batch, ' +
        'but they wont be able to create or submit the receipts.',
      method: 'POST',
      url: ':batchId/retract',
      response: undefined as unknown as {
        success: boolean;
      },
      body: undefined,
      query: undefined,
    },
  },
} as const;

export type ReceiptBatchControllerResultTypes = {
  [key in keyof (typeof ReceiptBatchControllerDefinition)['endpoints']]: (typeof ReceiptBatchControllerDefinition)['endpoints'][key]['response'];
};
export type ReceiptBatchControllerQueryTypes = {
  [key in keyof (typeof ReceiptBatchControllerDefinition)['endpoints']]: (typeof ReceiptBatchControllerDefinition)['endpoints'][key]['query'];
};
export type ReceiptBatchControllerBodyTypes = {
  [key in keyof (typeof ReceiptBatchControllerDefinition)['endpoints']]: (typeof ReceiptBatchControllerDefinition)['endpoints'][key]['body'];
};
